import { useEffect, useState } from "react";

/**
 * 기기(OS)가 정한 글자 배율 — 안드로이드 네이티브가 `--device-font-scale` 로 넣어 준다.
 *
 * 2026-09-23 실기 확인: 갤럭시에서 「설정 > 디스플레이 > 글자 크기 = 가장 크게」로 두면
 * 이 앱의 **글자만** 커졌다. 상자는 전부 하드 px(`h-6`·`w-[38px]`·`h-[420px]`)이라 글자만
 * 자라면 행이 넘쳐 잘린다 — 사용자가 말한 「넘침·잘림」이 바로 이것이다.
 *
 * 그래서 네이티브에서 WebView 자체 확대는 끄고(setTextZoom 100), OS 배율만 이 변수로
 * 받아 **화면 전체를 같은 비율로** 키운다(AppShell 의 transform 배율에 곱해진다).
 * 글자와 상자가 함께 커지므로 OS 설정은 지키면서 아무것도 잘리지 않는다.
 *
 * ⚠️ **배율을 그대로 쓰면 안 된다.** AppShell 은 `transform: scale()` + `width: 100/scale%`
 * 로 키우므로, 배율을 올리면 **레이아웃 폭이 그만큼 줄어든다**(390px 폰에 1.35 를 주면
 * 289px 짜리 화면을 그린 뒤 확대하는 셈). 이 앱은 이미 360px 에서도 빠듯한 자리가 있어
 * (경보 피드 한 행이 330px 을 요구한다) 289px 로 내려가면 **글자 잘림을 폭 잘림으로 바꾸는**
 * 꼴이 된다. 그래서 OS 배율을 **절반 남짓만 반영**하고 상한을 1.15 로 묶는다 —
 * 「가장 크게」(≈1.3)에서 글자는 약 13 % 커지고 레이아웃 폭은 344px 로, 앱이 감당하는
 * 범위 안에 남는다. OS 설정을 전부는 못 따라가지만, 따라가는 척하며 깨뜨리지는 않는다.
 *
 * 웹·iOS 에서는 변수가 없어 항상 1 — 기존 동작 그대로다.
 */
/** OS 배율을 얼마나 반영할지. 1 = 그대로(레이아웃이 좁아져 깨진다), 0 = 무시. */
const DAMPING = 0.45;
const MIN_SCALE = 0.95;
const MAX_SCALE = 1.15;

export function useDeviceFontScale(): number {
  const [scale, setScale] = useState(() => read());

  useEffect(() => {
    // 네이티브 주입은 페이지 로드보다 늦을 수 있고(onResume 재주입), 사용자가 설정을
    // 바꾸면 값이 갱신된다. style 속성 변화를 지켜본다 — 폴링보다 정확하고 싸다.
    const el = document.documentElement;
    const obs = new MutationObserver(() => {
      const next = read();
      setScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    });
    obs.observe(el, { attributes: true, attributeFilter: ["style"] });
    return () => obs.disconnect();
  }, []);

  return scale;
}

function read(): number {
  try {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue("--device-font-scale")
      .trim();
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n) || n <= 0) return 1;
    // OS 배율을 절반 남짓만 반영한다(위 ⚠️ 참조). 1.3 → 1.135.
    const damped = 1 + (n - 1) * DAMPING;
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, damped));
  } catch {
    return 1;                       // getComputedStyle 이 막힌 환경 — 배율 없음으로 본다
  }
}
